import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import { of } from 'rxjs';
import { environment } from '../../environments/environment';
import { CryptoService } from './crypto.service';

export interface UserProfile {
  name: string;
  email: string;
  picture: string;
  roles: string[];
  balance: number | null;
  org_group?: string;
  schema_admin?: boolean;
  tradefinance?: boolean;
  is_new?: boolean;
}

export interface KeypairStatus {
  has_keypair: boolean;
  keypair_created_at?: string;
  key_type?: 'server' | 'client';
  key_active_slot?: 'A' | 'B';
  rotation_status?: 'idle' | 'rotating';
  has_slot_b?: boolean;
}

export interface KeySlotData {
  public_key_pem: string;
  encrypted_private_key: string;
  private_key_salt: string;
  private_key_nonce: string;
}

/** Two-slot keypair data returned by GET /account/keypair-data */
export interface KeypairData {
  slot_a?: KeySlotData;
  slot_b?: KeySlotData;
}

export interface RotationStatus {
  rotation_status: 'idle' | 'rotating';
  key_active_slot: 'A' | 'B';
  retiring_slot?: 'A' | 'B';
  dossiers_pending?: number;
  has_slot_a: boolean;
  has_slot_b: boolean;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private readonly apiUrl = environment.apiBaseUrl;
  private _user = new BehaviorSubject<UserProfile | null>(null);

  user$: Observable<UserProfile | null> = this._user.asObservable();

  constructor(private http: HttpClient, private cryptoService: CryptoService) {}

  /** Check session on app load — silently resolves to null if not logged in. */
  checkSession(): Observable<UserProfile | null> {
    return this.http.get<UserProfile>(`${this.apiUrl}/auth/me`, { withCredentials: true }).pipe(
      tap(user => {
        // Seed with no balance; refreshProfile() will fill it in
        this._user.next({ ...user, balance: null });
        this.refreshProfile();
      }),
      catchError(() => {
        this._user.next(null);
        return of(null);
      })
    );
  }

  /** Fetch /profile to get balance (get-or-create user in DB). Updates user$ in place. */
  refreshProfile(): void {
    if (!this._user.value) return;
    this.http.get<any>(`${this.apiUrl}/profile`, { withCredentials: true }).pipe(
      catchError(() => of(null))
    ).subscribe(profile => {
      if (profile && this._user.value) {
        this._user.next({
          ...this._user.value,
          balance: profile.balance,
          tradefinance: profile.tradefinance ?? this._user.value.tradefinance,
        });
        if (profile.is_new && typeof (window as any).gtag === 'function') {
          (window as any).gtag('event', 'registration_complete');
        }
      }
    });
  }

  get user(): UserProfile | null {
    return this._user.value;
  }

  get isAuthenticated(): boolean {
    return this._user.value !== null;
  }

  get isDeveloper(): boolean {
    return this._user.value?.roles?.includes('developer') ?? false;
  }

  hasRole(role: string): boolean {
    return this._user.value?.roles?.includes(role) ?? false;
  }

  login(): void {
    window.location.href = `${this.apiUrl}/auth/login`;
  }

  loginWithGoogle(): void {
    window.location.href = `${this.apiUrl}/auth/login?idp=google`;
  }

  register(): void {
    window.location.href = `${this.apiUrl}/auth/register`;
  }

  registerWithGoogle(): void {
    window.location.href = `${this.apiUrl}/auth/register?idp=google`;
  }

  logout(): void {
    // Wipe cached private key and unlocked dossier set
    this.cryptoService.clearCache();

    this.http.post<{ message: string; logout_url?: string }>(
      `${this.apiUrl}/auth/logout`,
      {},
      { withCredentials: true }
    )
      .pipe(catchError(() => of({ message: 'Logged out', logout_url: undefined })))
      .subscribe((response) => {
        this._user.next(null);
        window.location.href = response.logout_url || environment.frontendUrl;
      });
  }

  // --- Keypair & DEK unlock ---

  /** Generate a server-side keypair encrypted with the user's password. */
  createKeypair(password: string): Observable<any> {
    return this.http.post<any>(
      `${this.apiUrl}/account/keypair`,
      { password },
      { withCredentials: true },
    );
  }

  /** Fetch keypair status (has_keypair, key_type, created_at). */
  getKeypairStatus(): Observable<KeypairStatus> {
    return this.http.get<KeypairStatus>(
      `${this.apiUrl}/account/keypair`,
      { withCredentials: true },
    );
  }

  /** Fetch encrypted private key material for client-side decryption. */
  getKeypairData(): Observable<KeypairData> {
    return this.http.get<KeypairData>(
      `${this.apiUrl}/account/keypair-data`,
      { withCredentials: true },
    );
  }

  /** Send the client-decrypted DEK to the server to set the dd_dek cookie. */
  unlockDek(dossierId: string, dekHex: string): Observable<{ status: string; dossier_id: string }> {
    return this.http.post<{ status: string; dossier_id: string }>(
      `${this.apiUrl}/account/unlock-dek`,
      { dek: dekHex, dossier_id: dossierId },
      { withCredentials: true },
    );
  }

  /** Fetch the current keypair rotation state. */
  getRotationStatus(): Observable<RotationStatus> {
    return this.http.get<RotationStatus>(
      `${this.apiUrl}/account/keypair/rotation-status`,
      { withCredentials: true },
    );
  }

  /**
   * Initiate keypair rotation. The caller must first generate a new RSA-4096
   * keypair in the browser and encrypt the private key with the current password.
   */
  initiateRotation(body: {
    public_key_pem: string;
    encrypted_private_key: string;
    private_key_salt: string;
    private_key_nonce: string;
  }): Observable<{ new_slot: string; retiring_slot: string; dossiers_pending: number }> {
    return this.http.post<{ new_slot: string; retiring_slot: string; dossiers_pending: number }>(
      `${this.apiUrl}/account/keypair/rotate`,
      body,
      { withCredentials: true },
    );
  }

  /**
   * Re-wrap a single dossier's DEK with the new slot's public key.
   * Returns the remaining dossier count and rotation status after this call.
   */
  rewrapDossierDek(
    dossierId: string,
    encryptedDek: string,
  ): Observable<{ remaining: number; rotation_status: string }> {
    return this.http.patch<{ remaining: number; rotation_status: string }>(
      `${this.apiUrl}/account/keypair/rotate/dossier/${dossierId}`,
      { encrypted_dek: encryptedDek },
      { withCredentials: true },
    );
  }

  /**
   * Store re-encrypted private key material for all populated slots after a
   * password change. Blocked server-side during active rotation.
   */
  changeKeyPassword(slots: {
    slot_a?: { encrypted_private_key: string; private_key_salt: string; private_key_nonce: string };
    slot_b?: { encrypted_private_key: string; private_key_salt: string; private_key_nonce: string };
  }): Observable<{ status: string }> {
    return this.http.put<{ status: string }>(
      `${this.apiUrl}/account/keypair/password`,
      slots,
      { withCredentials: true },
    );
  }
}
